import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { CONTEUDO_CHAVES } from '@/api/adminApi'
import {
  createTarefa,
  createTarefaCategoria,
  deleteTarefa,
  deleteTarefaCategoria,
  getTarefaCategorias,
  getTarefaConfig,
  getTarefas,
  putTarefaConfig,
  updateTarefa,
  updateTarefaCategoria,
  type Tarefa,
  type TarefaCategoria,
  type TarefaConfig,
  type TarefaStatus,
} from '@/api/tarefasApi'
import { isAdmin } from '@/utils/adminAuth'
import { isProfissional } from '@/utils/profissionalAuth'
import { useConteudoFlag } from '@/utils/featureFlags'
import { useSaveFeedback } from '@/hooks/useSaveFeedback'
import { hojeStr } from '@/utils/date'
import styles from './TarefasPage.module.css'

type Tab = 'categorias' | 'tarefas' | 'calendario' | 'config'

const CORES = ['#38bdf8', '#34d399', '#fbbf24', '#f472b6', '#a78bfa', '#fb7185', '#2dd4bf']

const STATUS_LABEL: Record<TarefaStatus, string> = {
  pendente: 'Pendente',
  em_progresso: 'Em progresso',
  concluida: 'Concluída',
}

function staffHomePath(): string {
  if (isAdmin()) return '/admin/dashboard'
  if (isProfissional()) return '/profissional/painel'
  return '/'
}

function formatData(iso: string): string {
  const [y, m, d] = iso.split('-')
  if (!y || !m || !d) return iso
  return `${d}/${m}/${y}`
}

function monthLabel(year: number, month: number): string {
  return new Date(year, month, 1).toLocaleDateString('pt-PT', {
    month: 'long',
    year: 'numeric',
  })
}

function daysInMonth(year: number, month: number): number {
  return new Date(year, month + 1, 0).getDate()
}

function toIso(year: number, month: number, day: number): string {
  return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`
}

export function TarefasPage() {
  const navigate = useNavigate()
  const { enabled, loading: featureLoading } = useConteudoFlag(CONTEUDO_CHAVES.tarefas_enabled)
  const { notifySaved, notifyError, notifyDeleted } = useSaveFeedback()

  const [tab, setTab] = useState<Tab>('tarefas')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [categorias, setCategorias] = useState<TarefaCategoria[]>([])
  const [tarefas, setTarefas] = useState<Tarefa[]>([])
  const [config, setConfig] = useState<TarefaConfig>({
    mostrar_concluidas: true,
    exigir_categoria: false,
  })

  /* Categorias form */
  const [catNome, setCatNome] = useState('')
  const [catCor, setCatCor] = useState(CORES[0])
  const [editingCat, setEditingCat] = useState<TarefaCategoria | null>(null)
  const [savingCat, setSavingCat] = useState(false)

  /* Tarefas form */
  const [tarNome, setTarNome] = useState('')
  const [tarDesc, setTarDesc] = useState('')
  const [tarData, setTarData] = useState(hojeStr())
  const [tarHora, setTarHora] = useState('')
  const [tarCat, setTarCat] = useState<number | ''>('')
  const [tarStatus, setTarStatus] = useState<TarefaStatus>('pendente')
  const [editingTar, setEditingTar] = useState<Tarefa | null>(null)
  const [savingTar, setSavingTar] = useState(false)
  const [filtroCat, setFiltroCat] = useState<number | ''>('')

  /* Calendário */
  const today = hojeStr()
  const [calYear, setCalYear] = useState(() => Number(today.slice(0, 4)))
  const [calMonth, setCalMonth] = useState(() => Number(today.slice(5, 7)) - 1)
  const [selectedDay, setSelectedDay] = useState(today)
  const [monthTarefas, setMonthTarefas] = useState<Tarefa[]>([])

  /* Config */
  const [savingConfig, setSavingConfig] = useState(false)

  useEffect(() => {
    if (!isAdmin() && !isProfissional()) {
      navigate('/', { replace: true })
    }
  }, [navigate])

  useEffect(() => {
    if (!featureLoading && !enabled) {
      navigate(staffHomePath(), { replace: true })
    }
  }, [featureLoading, enabled, navigate])

  const loadCore = useCallback(async () => {
    setError(null)
    try {
      const [cats, list, cfg] = await Promise.all([
        getTarefaCategorias(true),
        getTarefas(filtroCat ? { categoria_id: Number(filtroCat) } : {}),
        getTarefaConfig(),
      ])
      setCategorias(cats)
      setTarefas(list)
      setConfig(cfg)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erro ao carregar tarefas')
    } finally {
      setLoading(false)
    }
  }, [filtroCat])

  useEffect(() => {
    if (featureLoading || !enabled) return
    void loadCore()
  }, [featureLoading, enabled, loadCore])

  const loadMonth = useCallback(async () => {
    const de = toIso(calYear, calMonth, 1)
    const ate = toIso(calYear, calMonth, daysInMonth(calYear, calMonth))
    try {
      const list = await getTarefas({ de, ate })
      setMonthTarefas(list)
    } catch (e) {
      notifyError(e, 'Erro ao carregar calendário')
    }
  }, [calYear, calMonth, notifyError])

  useEffect(() => {
    if (featureLoading || !enabled || tab !== 'calendario') return
    void loadMonth()
  }, [featureLoading, enabled, tab, loadMonth])

  const countsByDay = useMemo(() => {
    const m = new Map<string, number>()
    for (const t of monthTarefas) {
      m.set(t.data_tarefa, (m.get(t.data_tarefa) || 0) + 1)
    }
    return m
  }, [monthTarefas])

  const dayTarefas = useMemo(
    () => monthTarefas.filter((t) => t.data_tarefa === selectedDay),
    [monthTarefas, selectedDay]
  )

  const resetCatForm = () => {
    setEditingCat(null)
    setCatNome('')
    setCatCor(CORES[0])
  }

  const resetTarForm = () => {
    setEditingTar(null)
    setTarNome('')
    setTarDesc('')
    setTarData(hojeStr())
    setTarHora('')
    setTarCat('')
    setTarStatus('pendente')
  }

  const saveCategoria = async () => {
    if (!catNome.trim()) {
      notifyError(new Error('Indique o nome da categoria.'), 'Categoria')
      return
    }
    setSavingCat(true)
    try {
      if (editingCat) {
        await updateTarefaCategoria(editingCat.id, {
          nome: catNome.trim(),
          cor: catCor,
        })
        notifySaved('Categoria actualizada.')
      } else {
        await createTarefaCategoria({ nome: catNome.trim(), cor: catCor })
        notifySaved('Categoria criada.')
      }
      resetCatForm()
      await loadCore()
    } catch (e) {
      notifyError(e, 'Erro ao guardar categoria')
    } finally {
      setSavingCat(false)
    }
  }

  const editCategoria = (c: TarefaCategoria) => {
    setEditingCat(c)
    setCatNome(c.nome)
    setCatCor(c.cor || CORES[0])
  }

  const removeCategoria = async (c: TarefaCategoria) => {
    if (!confirm(`Excluir a categoria “${c.nome}”?`)) return
    try {
      await deleteTarefaCategoria(c.id)
      notifyDeleted('Categoria excluída.')
      if (editingCat?.id === c.id) resetCatForm()
      await loadCore()
      if (tab === 'calendario') await loadMonth()
    } catch (e) {
      notifyError(e, 'Erro ao excluir categoria')
    }
  }

  const saveTarefa = async () => {
    if (!tarNome.trim()) {
      notifyError(new Error('Indique o nome da tarefa.'), 'Tarefa')
      return
    }
    if (!tarData) {
      notifyError(new Error('Indique a data.'), 'Tarefa')
      return
    }
    if (config.exigir_categoria && !tarCat) {
      notifyError(new Error('Seleccione uma categoria.'), 'Tarefa')
      return
    }
    setSavingTar(true)
    try {
      const payload = {
        nome: tarNome.trim(),
        descricao: tarDesc.trim() || null,
        data_tarefa: tarData,
        hora: tarHora || null,
        status: tarStatus,
        categoria_id: tarCat === '' ? null : Number(tarCat),
      }
      if (editingTar) {
        await updateTarefa(editingTar.id, payload)
        notifySaved('Tarefa actualizada.')
      } else {
        await createTarefa(payload)
        notifySaved('Tarefa criada.')
      }
      resetTarForm()
      await loadCore()
      if (tab === 'calendario') await loadMonth()
    } catch (e) {
      notifyError(e, 'Erro ao guardar tarefa')
    } finally {
      setSavingTar(false)
    }
  }

  const editTarefa = (t: Tarefa) => {
    setEditingTar(t)
    setTarNome(t.nome)
    setTarDesc(t.descricao || '')
    setTarData(t.data_tarefa)
    setTarHora(t.hora || '')
    setTarCat(t.categoria_id ?? '')
    setTarStatus(t.status)
    setTab('tarefas')
  }

  const changeStatus = async (t: Tarefa, status: TarefaStatus) => {
    try {
      await updateTarefa(t.id, { status })
      await loadCore()
      if (tab === 'calendario') await loadMonth()
    } catch (e) {
      notifyError(e, 'Erro ao actualizar estado')
    }
  }

  const removeTarefa = async (t: Tarefa) => {
    if (!confirm(`Excluir a tarefa “${t.nome}”?`)) return
    try {
      await deleteTarefa(t.id)
      notifyDeleted('Tarefa excluída.')
      if (editingTar?.id === t.id) resetTarForm()
      await loadCore()
      if (tab === 'calendario') await loadMonth()
    } catch (e) {
      notifyError(e, 'Erro ao excluir tarefa')
    }
  }

  const saveConfig = async (next: TarefaConfig) => {
    setConfig(next)
    setSavingConfig(true)
    try {
      const saved = await putTarefaConfig(next)
      setConfig(saved)
      notifySaved('Configurações guardadas.')
      await loadCore()
      if (tab === 'calendario') await loadMonth()
    } catch (e) {
      notifyError(e, 'Erro ao guardar configurações')
      await loadCore()
    } finally {
      setSavingConfig(false)
    }
  }

  const shiftMonth = (delta: number) => {
    const d = new Date(calYear, calMonth + delta, 1)
    setCalYear(d.getFullYear())
    setCalMonth(d.getMonth())
  }

  const calCells = useMemo(() => {
    const firstDow = new Date(calYear, calMonth, 1).getDay()
    const startPad = (firstDow + 6) % 7 // segunda = 0
    const total = daysInMonth(calYear, calMonth)
    const cells: Array<{ day: number | null; iso: string | null }> = []
    for (let i = 0; i < startPad; i++) cells.push({ day: null, iso: null })
    for (let d = 1; d <= total; d++) {
      cells.push({ day: d, iso: toIso(calYear, calMonth, d) })
    }
    while (cells.length % 7 !== 0) cells.push({ day: null, iso: null })
    return cells
  }, [calYear, calMonth])

  const stats = useMemo(() => {
    const pendentes = tarefas.filter((t) => t.status === 'pendente').length
    const progresso = tarefas.filter((t) => t.status === 'em_progresso').length
    const concluidas = tarefas.filter((t) => t.status === 'concluida').length
    return { pendentes, progresso, concluidas, total: tarefas.length }
  }, [tarefas])

  const statusClass = (status: TarefaStatus) => {
    if (status === 'pendente') return styles.statusPendente
    if (status === 'em_progresso') return styles.statusEm_progresso
    return styles.statusConcluida
  }

  const renderTarefaCard = (t: Tarefa, compact = false) => {
    const accent = t.categoria_cor || 'var(--accent)'
    const done = t.status === 'concluida'
    return (
      <article
        key={t.id}
        className={`${styles.item} ${done ? styles.itemDone : ''}`}
        style={{ ['--badge-color' as string]: accent }}
      >
        <div className={styles.itemAccent} style={{ background: accent }} />
        <div className={styles.itemBody}>
          <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
            <button
              type="button"
              className={`${styles.checkBtn} ${done ? styles.checkBtnOn : ''}`}
              aria-label={done ? 'Marcar como pendente' : 'Marcar como concluída'}
              onClick={() => void changeStatus(t, done ? 'pendente' : 'concluida')}
            >
              ✓
            </button>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div className={styles.itemTitle}>{t.nome}</div>
              <div className={styles.itemMeta}>
                <span className={styles.dateChip}>
                  {formatData(t.data_tarefa)}
                  {t.hora ? ` · ${t.hora}` : ''}
                </span>
                {t.categoria_nome && (
                  <span className={styles.badge} style={{ ['--badge-color' as string]: accent }}>
                    <span className={styles.dot} />
                    {t.categoria_nome}
                  </span>
                )}
                <span className={`${styles.statusChip} ${statusClass(t.status)}`}>
                  {STATUS_LABEL[t.status]}
                </span>
              </div>
              {!compact && t.descricao && <p className={styles.itemDesc}>{t.descricao}</p>}
            </div>
          </div>
        </div>
        <div className={styles.itemActions}>
          {!compact && (
            <select
              className={styles.statusSelect}
              value={t.status}
              onChange={(e) => void changeStatus(t, e.target.value as TarefaStatus)}
              aria-label="Estado da tarefa"
            >
              {(Object.keys(STATUS_LABEL) as TarefaStatus[]).map((s) => (
                <option key={s} value={s}>
                  {STATUS_LABEL[s]}
                </option>
              ))}
            </select>
          )}
          <button type="button" className={styles.btnIcon} title="Editar" onClick={() => editTarefa(t)}>
            ✎
          </button>
          {!compact && (
            <button
              type="button"
              className={styles.btnDanger}
              title="Excluir"
              onClick={() => void removeTarefa(t)}
            >
              ×
            </button>
          )}
        </div>
      </article>
    )
  }

  if (featureLoading || !enabled) return null
  if (!isAdmin() && !isProfissional()) return null

  const catsAtivas = categorias.filter((c) => !!c.ativo)
  const selectedDayLabel = new Date(`${selectedDay}T12:00:00`).toLocaleDateString('pt-PT', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  })

  return (
    <div className={styles.page}>
      <header className={styles.hero}>
        <div className={styles.heroGlow} aria-hidden />
        <p className={styles.heroKicker}>Equipa</p>
        <h1 className={styles.heroTitle}>Tarefas</h1>
        <p className={styles.heroSub}>
          Organize o dia da equipa — categorias, prazos e calendário num só sítio.
        </p>
        {!loading && (
          <div className={styles.stats}>
            <div className={styles.stat}>
              <span className={styles.statValue}>{stats.pendentes}</span>
              <span className={styles.statLabel}>Pendentes</span>
            </div>
            <div className={styles.stat}>
              <span className={styles.statValue}>{stats.progresso}</span>
              <span className={styles.statLabel}>Em curso</span>
            </div>
            <div className={styles.stat}>
              <span className={styles.statValue}>{stats.concluidas}</span>
              <span className={styles.statLabel}>Concluídas</span>
            </div>
          </div>
        )}
      </header>

      <nav className={styles.tabs} aria-label="Secções de tarefas">
        {(
          [
            ['tarefas', 'Tarefas', '✓'],
            ['categorias', 'Categorias', '◈'],
            ['calendario', 'Calendário', '▦'],
            ['config', 'Ajustes', '⚙'],
          ] as const
        ).map(([id, label, icon]) => (
          <button
            key={id}
            type="button"
            className={`${styles.tab} ${tab === id ? styles.tabActive : ''}`}
            onClick={() => setTab(id)}
          >
            <span className={styles.tabIcon} aria-hidden>
              {icon}
            </span>
            {label}
          </button>
        ))}
      </nav>

      {error && <p className={styles.error}>{error}</p>}
      {loading && (
        <div className={styles.loading}>
          <span className={styles.spinner} aria-hidden />
          A carregar…
        </div>
      )}

      {!loading && tab === 'categorias' && (
        <section className={styles.panel}>
          <div className={styles.sectionHead}>
            <h2 className={styles.panelTitle}>
              {editingCat ? 'Editar categoria' : 'Nova categoria'}
            </h2>
            <p className={styles.panelHint}>Agrupe tarefas por tipo, área ou prioridade.</p>
          </div>
          <div className={styles.form}>
            <label className={styles.label}>
              Nome
              <input
                className={styles.input}
                value={catNome}
                onChange={(e) => setCatNome(e.target.value)}
                placeholder="Ex.: Clínica, Administrativo…"
              />
            </label>
            <label className={styles.label}>
              Cor
              <div className={styles.colorRow}>
                {CORES.map((c) => (
                  <button
                    key={c}
                    type="button"
                    className={`${styles.colorSwatch} ${catCor === c ? styles.colorSwatchOn : ''}`}
                    style={{ background: c, color: c }}
                    aria-label={`Cor ${c}`}
                    onClick={() => setCatCor(c)}
                  />
                ))}
              </div>
            </label>
            <div className={styles.formActions}>
              <button
                type="button"
                className={styles.btnPrimary}
                disabled={savingCat}
                onClick={() => void saveCategoria()}
              >
                {savingCat ? 'A guardar…' : editingCat ? 'Guardar alterações' : 'Criar categoria'}
              </button>
              {editingCat && (
                <button type="button" className={styles.btnGhost} onClick={resetCatForm}>
                  Cancelar
                </button>
              )}
            </div>
          </div>

          <hr className={styles.divider} />

          <div className={styles.sectionHead}>
            <h2 className={styles.panelTitle}>Todas as categorias</h2>
            <p className={styles.panelHint}>
              {categorias.length === 0
                ? 'Ainda vazias — crie a primeira acima.'
                : `${categorias.length} categoria${categorias.length > 1 ? 's' : ''}`}
            </p>
          </div>
          {categorias.length === 0 ? (
            <div className={styles.empty}>
              <span className={styles.emptyIcon} aria-hidden>
                ◈
              </span>
              Sem categorias por agora.
            </div>
          ) : (
            <div className={styles.list}>
              {categorias.map((c) => (
                <div key={c.id} className={styles.catCard}>
                  <div
                    className={styles.catMark}
                    style={{ background: c.cor || 'var(--accent)' }}
                    aria-hidden
                  >
                    {(c.nome.trim()[0] || '?').toUpperCase()}
                  </div>
                  <div className={styles.catInfo}>
                    <span className={styles.itemTitle}>{c.nome}</span>
                    <span className={styles.itemMeta}>{c.ativo ? 'Activa' : 'Inactiva'}</span>
                  </div>
                  <div className={styles.itemActions}>
                    <button
                      type="button"
                      className={styles.btnIcon}
                      title="Editar"
                      onClick={() => editCategoria(c)}
                    >
                      ✎
                    </button>
                    <button
                      type="button"
                      className={styles.btnDanger}
                      title="Excluir"
                      onClick={() => void removeCategoria(c)}
                    >
                      ×
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      )}

      {!loading && tab === 'tarefas' && (
        <section className={styles.panel}>
          <div className={styles.sectionHead}>
            <h2 className={styles.panelTitle}>{editingTar ? 'Editar tarefa' : 'Nova tarefa'}</h2>
            <p className={styles.panelHint}>Nome, data e categoria — o essencial para a equipa.</p>
          </div>
          <div className={styles.form}>
            <label className={styles.label}>
              Nome
              <input
                className={styles.input}
                value={tarNome}
                onChange={(e) => setTarNome(e.target.value)}
                placeholder="Ex.: Confirmar stock, Ligar ao cliente…"
              />
            </label>
            <label className={styles.label}>
              Descrição
              <textarea
                className={styles.textarea}
                value={tarDesc}
                onChange={(e) => setTarDesc(e.target.value)}
                placeholder="Detalhes opcionais"
              />
            </label>
            <div className={styles.formRow}>
              <label className={styles.label}>
                Data
                <input
                  type="date"
                  className={styles.input}
                  value={tarData}
                  onChange={(e) => setTarData(e.target.value)}
                />
              </label>
              <label className={styles.label}>
                Hora (opcional)
                <input
                  type="time"
                  className={styles.input}
                  value={tarHora}
                  onChange={(e) => setTarHora(e.target.value)}
                />
              </label>
            </div>
            <div className={styles.formRow}>
              <label className={styles.label}>
                Categoria{config.exigir_categoria ? ' *' : ''}
                <select
                  className={styles.select}
                  value={tarCat === '' ? '' : String(tarCat)}
                  onChange={(e) => setTarCat(e.target.value ? Number(e.target.value) : '')}
                >
                  <option value="">Sem categoria</option>
                  {catsAtivas.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.nome}
                    </option>
                  ))}
                </select>
              </label>
              <label className={styles.label}>
                Estado
                <select
                  className={styles.select}
                  value={tarStatus}
                  onChange={(e) => setTarStatus(e.target.value as TarefaStatus)}
                >
                  {(Object.keys(STATUS_LABEL) as TarefaStatus[]).map((s) => (
                    <option key={s} value={s}>
                      {STATUS_LABEL[s]}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <div className={styles.formActions}>
              <button
                type="button"
                className={styles.btnPrimary}
                disabled={savingTar}
                onClick={() => void saveTarefa()}
              >
                {savingTar ? 'A guardar…' : editingTar ? 'Guardar alterações' : 'Criar tarefa'}
              </button>
              {editingTar && (
                <button type="button" className={styles.btnGhost} onClick={resetTarForm}>
                  Cancelar
                </button>
              )}
            </div>
          </div>

          <hr className={styles.divider} />

          <div className={styles.listHead}>
            <div className={styles.sectionHead}>
              <h2 className={styles.panelTitle}>Todas as tarefas</h2>
              <p className={styles.panelHint}>
                {stats.total === 0 ? 'Nenhuma ainda' : `${stats.total} no total`}
              </p>
            </div>
            <select
              className={`${styles.select} ${styles.filterSelect}`}
              value={filtroCat === '' ? '' : String(filtroCat)}
              onChange={(e) => setFiltroCat(e.target.value ? Number(e.target.value) : '')}
            >
              <option value="">Todas as categorias</option>
              {categorias.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.nome}
                </option>
              ))}
            </select>
          </div>

          {tarefas.length === 0 ? (
            <div className={styles.empty}>
              <span className={styles.emptyIcon} aria-hidden>
                ✓
              </span>
              Ainda não há tarefas. Crie a primeira acima.
            </div>
          ) : (
            <div className={styles.list}>{tarefas.map((t) => renderTarefaCard(t))}</div>
          )}
        </section>
      )}

      {!loading && tab === 'calendario' && (
        <section className={styles.panel}>
          <div className={styles.sectionHead}>
            <h2 className={styles.panelTitle}>Calendário</h2>
            <p className={styles.panelHint}>Toque num dia para ver o que está marcado.</p>
          </div>

          <div className={styles.calShell}>
            <div className={styles.calNav}>
              <button type="button" className={styles.btnIcon} onClick={() => shiftMonth(-1)} aria-label="Mês anterior">
                ‹
              </button>
              <span className={styles.calMonth}>{monthLabel(calYear, calMonth)}</span>
              <button type="button" className={styles.btnIcon} onClick={() => shiftMonth(1)} aria-label="Mês seguinte">
                ›
              </button>
            </div>

            <div className={styles.calGrid}>
              {['Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb', 'Dom'].map((d) => (
                <div key={d} className={styles.calDow}>
                  {d}
                </div>
              ))}
              {calCells.map((cell, i) => {
                if (!cell.day || !cell.iso) {
                  return <div key={`e-${i}`} className={`${styles.calDay} ${styles.calDayMuted}`} />
                }
                const count = countsByDay.get(cell.iso) || 0
                const isSelected = cell.iso === selectedDay
                const isToday = cell.iso === today
                return (
                  <button
                    key={cell.iso}
                    type="button"
                    className={[
                      styles.calDay,
                      isToday ? styles.calDayToday : '',
                      isSelected ? styles.calDaySelected : '',
                      count > 0 ? styles.calDayHasTasks : '',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                    onClick={() => setSelectedDay(cell.iso!)}
                  >
                    {cell.day}
                    {count > 0 && (
                      <span className={styles.calDots} aria-label={`${count} tarefas`}>
                        {Array.from({ length: Math.min(count, 3) }).map((_, di) => (
                          <span key={di} className={styles.calDot} />
                        ))}
                      </span>
                    )}
                  </button>
                )
              })}
            </div>
          </div>

          <div className={styles.dayPanel}>
            <div className={styles.daySummary}>
              <span className={styles.dayTitle} style={{ textTransform: 'capitalize' }}>
                {selectedDayLabel}
              </span>
              <span className={styles.dayCount}>
                {dayTarefas.length === 0
                  ? 'Livre'
                  : `${dayTarefas.length} tarefa${dayTarefas.length > 1 ? 's' : ''}`}
              </span>
            </div>

            <button
              type="button"
              className={styles.btnPrimary}
              onClick={() => {
                resetTarForm()
                setTarData(selectedDay)
                setTab('tarefas')
              }}
            >
              Criar tarefa neste dia
            </button>

            {dayTarefas.length === 0 ? (
              <div className={styles.empty}>Nada marcado neste dia.</div>
            ) : (
              <div className={styles.list}>{dayTarefas.map((t) => renderTarefaCard(t, true))}</div>
            )}
          </div>
        </section>
      )}

      {!loading && tab === 'config' && (
        <section className={styles.panel}>
          <div className={styles.sectionHead}>
            <h2 className={styles.panelTitle}>Configurações básicas</h2>
            <p className={styles.panelHint}>Ajustes simples do módulo — guardados de imediato.</p>
          </div>

          <div className={styles.configList}>
            <div className={styles.configRow}>
              <div className={styles.configText}>
                <span className={styles.configTitle}>Mostrar concluídas</span>
                <span className={styles.configDesc}>
                  Inclui tarefas concluídas nas listagens e no calendário.
                </span>
              </div>
              <label className={styles.toggle} aria-label="Mostrar concluídas">
                <input
                  type="checkbox"
                  checked={config.mostrar_concluidas}
                  disabled={savingConfig}
                  onChange={(e) =>
                    void saveConfig({ ...config, mostrar_concluidas: e.target.checked })
                  }
                />
                <span className={styles.toggleTrack}>
                  <span className={styles.toggleThumb} />
                </span>
              </label>
            </div>

            <div className={styles.configRow}>
              <div className={styles.configText}>
                <span className={styles.configTitle}>Exigir categoria</span>
                <span className={styles.configDesc}>
                  Obriga a escolher uma categoria ao criar ou editar tarefas.
                </span>
              </div>
              <label className={styles.toggle} aria-label="Exigir categoria">
                <input
                  type="checkbox"
                  checked={config.exigir_categoria}
                  disabled={savingConfig}
                  onChange={(e) =>
                    void saveConfig({ ...config, exigir_categoria: e.target.checked })
                  }
                />
                <span className={styles.toggleTrack}>
                  <span className={styles.toggleThumb} />
                </span>
              </label>
            </div>
          </div>
        </section>
      )}
    </div>
  )
}
